import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { registerUser, getAllBranches } from '../api/Api'; // Assuming registerUser and getAllBranches are imported correctly

const UserRegistration = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user', // Default role
    branches: [], // Array to hold selected branch IDs
  });
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await getAllBranches();
      if (response && Array.isArray(response.data)) {
        setAllBranches(response.data);
      } else {
        setAllBranches([]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error('Failed to load branches.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      if (checked) {
        setFormData((prev) => ({ ...prev, branches: [...prev.branches, value] }));
      } else {
        setFormData((prev) => ({ ...prev, branches: prev.branches.filter((branchId) => branchId !== value) }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Basic validation
    if (!formData.username || !formData.password || !formData.role) {
      toast.error('Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }
    // Ensure at least one branch is selected if branches are available
    if (allBranches.length > 0 && formData.branches.length === 0) {
        toast.error('Please assign at least one branch to the user.');
        setIsSubmitting(false);
        return;
    }
    // If no branches exist, allow registration without branch selection
    if (allBranches.length === 0 && formData.branches.length > 0) {
      // This case shouldn't happen if logic is correct, but for safety
      setFormData((prev) => ({ ...prev, branches: [] }));
    }


    try {
      const response = await registerUser(formData);
      if (response.success) {
        toast.success('User registered successfully!');
        // Reset form
        setFormData({ username: '', password: '', role: 'user', branches: [] });
      } else {
        toast.error(response.message || 'User registration failed.');
      }
    } catch (error) {
      console.error('Error during user registration:', error);
      toast.error('An error occurred during registration. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-screen-xl mx-auto bg-white rounded-xl shadow-md min-h-screen">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <h2 className="text-2xl font-bold mb-6 text-[#16bab1]">
        Register New User
      </h2>

      <div className="mb-8 p-6 border border-gray-100 rounded-xl bg-gray-50 shadow-sm">
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Username*</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Enter username"
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#16bab1] bg-white"
              required
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Password*</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter password"
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#16bab1] bg-white"
              required
            />
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Role*</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#16bab1] bg-white cursor-pointer"
              required
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          {/* Branch Selection */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-600 mb-2">Assign Branches*</label>
            {!loading && allBranches.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {allBranches.map((branch) => (
                  <div key={branch._id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`branch-${branch._id}`}
                      name="branches"
                      value={branch._id}
                      checked={formData.branches.includes(branch._id)}
                      onChange={handleInputChange}
                      className="mr-2 h-4 w-4 text-[#16bab1] border-gray-300 rounded focus:ring-[#16bab1]"
                    />
                    <label htmlFor={`branch-${branch._id}`} className="text-sm text-gray-700">
                      {branch.name}
                    </label>
                  </div>
                ))}
              </div>
            ) : loading ? (
              <div className="text-center text-gray-500">Loading branches...</div>
            ) : (
              <div className="text-center text-gray-500">
                No branches available to assign. Please add branches first.
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="md:col-span-2 flex justify-end gap-3 mt-4">
            <button
              type="submit"
              disabled={isSubmitting || !formData.username || !formData.password || !formData.role || (allBranches.length > 0 && formData.branches.length === 0)}
              className={`px-8 py-3 rounded-lg font-medium shadow-sm transition ${
                isSubmitting || !formData.username || !formData.password || !formData.role || (allBranches.length > 0 && formData.branches.length === 0)
                  ? 'bg-gray-400 text-white cursor-not-allowed'
                  : 'bg-[#16bab1] hover:bg-[#139a93] text-white'
              }`}
            >
              {isSubmitting ? 'Registering...' : 'Register User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserRegistration;
